import { IconMoodBoy, IconUser, IconUserHeart } from '@tabler/icons-react';
import { InsuredSubtype } from '@zorro/clients';
import {
  Accordion,
  AccordionStyleVariant,
} from '@zorro/design-system/v2/Accordion';
import { Text, brand } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';

import { useAnalytics } from '../../Analytics';
import { UseInsuredFormReturnType } from '../InsuredFormUtils';
import { ReadonlyDependentForm } from './ReadonlyDependentForm';
import { ReadonlyEmployeeForm } from './ReadonlyEmployeeForm';

type Props = {
  insuredForm: UseInsuredFormReturnType;
  onboardingPeriodId?: string;
  isEmployeeMode?: boolean;
  shouldOpenEmployeeFormByDefault?: boolean;
  shouldShowEmploymentInfo?: boolean;
  isSelfEnrollMode?: boolean;
  withAgeAtPlanStart?: boolean;
  accordionVariant?: AccordionStyleVariant;
};

export const ReadonlyInsuredForm = ({
  insuredForm,
  onboardingPeriodId,
  isEmployeeMode = false,
  shouldOpenEmployeeFormByDefault = false,
  shouldShowEmploymentInfo = true,
  isSelfEnrollMode = false,
  withAgeAtPlanStart = false,
  accordionVariant,
}: Props) => {
  const { sendEvent } = useAnalytics();

  const {
    employeeForm,
    spouseForm,
    dependentsForm,
    targetEnrollmentDate,
    employeeZipCode,
    employeeFipsCode,
  } = insuredForm;
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: onboardingPeriodId && [onboardingPeriodId],
  });
  const [accordionValue, setAccordionValue] = useState<string[]>(
    shouldOpenEmployeeFormByDefault ? ['Employee'] : []
  );

  const { fields } = useFieldArray({
    control: dependentsForm.control,
    name: 'dependents',
  });

  const sendAccordionEvent = async (
    memberType: string,
    currentValue: string,
    eventName: string
  ) => {
    await sendEvent(eventName, {
      member_type: memberType,
      onboarding_period_id: onboardingPeriodId,
      action_type: accordionValue.includes(currentValue)
        ? 'collapse'
        : 'expand',
    });
  };

  const coveredInsuredPeople = benefits?.majorMedicalBenefit?.insuredPeople;

  const shouldShowSpouseForm = coveredInsuredPeople
    ? Boolean(coveredInsuredPeople.spouse)
    : insuredForm.spouseStatus.isSpouseActive;

  const coveredDependentIds =
    coveredInsuredPeople?.dependents?.map((dep) => dep.id) ?? [];
  const coveredDependents = coveredInsuredPeople
    ? fields.filter((field) =>
        coveredDependentIds.includes(field.insuredId ?? '')
      )
    : fields;

  const [employeeFirstName, employeeLastName] = employeeForm.watch([
    'firstName',
    'lastName',
  ]);
  const [spouseFirstName, spouseLastName] = spouseForm.watch([
    'firstName',
    'lastName',
  ]);

  return (
    <Accordion
      styleVariant={accordionVariant}
      multiple
      value={accordionValue}
      onChange={(value) => {
        setAccordionValue(value);
      }}
    >
      <Accordion.Item
        value="Employee"
        onClick={async () => {
          await sendAccordionEvent(
            'self',
            'Employee',
            isSelfEnrollMode ? 'self_enroll_expand_employee' : 'expand_collapse'
          );
        }}
      >
        <Accordion.Control
          icon={<IconUser color={brand.zorroGray400} />}
          p={16}
        >
          <Text fw={600}>
            {isEmployeeMode ? 'You' : 'Employee'}
            <Text ml="0.25rem" fw={400} c="zorroGray.4" isMasked>
              ({employeeFirstName} {employeeLastName})
            </Text>
          </Text>
        </Accordion.Control>
        <Accordion.Panel>
          <ReadonlyEmployeeForm
            employeeForm={employeeForm}
            onboardingPeriodId={onboardingPeriodId}
            shouldShowEmploymentInfo={shouldShowEmploymentInfo}
            targetEnrollmentDate={targetEnrollmentDate}
            withAgeAtPlanStart={withAgeAtPlanStart}
            employeeZipCode={employeeZipCode}
            employeeFipsCode={employeeFipsCode}
          />
        </Accordion.Panel>
      </Accordion.Item>
      {shouldShowSpouseForm && (
        <Accordion.Item
          value="Spouse"
          onClick={async () => {
            await sendAccordionEvent(
              'spouse',
              'Spouse',
              isSelfEnrollMode ? 'self_enroll_expand_spouse' : 'expand_collapse'
            );
          }}
        >
          <Accordion.Control
            icon={<IconUserHeart color={brand.zorroGray400} />}
            p={16}
          >
            <Text fw={600}>
              {spouseForm.watch('subtype') === InsuredSubtype.DOMESTIC_PARTNER
                ? 'Domestic partner'
                : 'Spouse'}
              <Text ml="0.25rem" fw={400} c="zorroGray.4" isMasked>
                ({spouseFirstName} {spouseLastName})
              </Text>
            </Text>
          </Accordion.Control>
          <Accordion.Panel>
            <ReadonlyDependentForm
              fields={spouseForm.watch()}
              targetEnrollmentDate={targetEnrollmentDate}
              withAgeAtPlanStart={withAgeAtPlanStart}
              isEmployeeMode={isEmployeeMode}
            />
          </Accordion.Panel>
        </Accordion.Item>
      )}

      {coveredDependents.map((dependent) => {
        const disabilityLabel =
          dependent.subtype === InsuredSubtype.OTHER_DEPENDENT
            ? 'dependent with a disability'
            : 'adult child with a disability';

        return (
          <Accordion.Item
            key={dependent.id}
            value={dependent.id}
            onClick={async () => {
              await sendAccordionEvent(
                'children',
                dependent.id,
                isSelfEnrollMode
                  ? 'self_enroll_expand_child'
                  : 'expand_collapse'
              );
            }}
          >
            <Accordion.Control
              icon={<IconMoodBoy color={brand.zorroGray400} />}
              p={16}
            >
              <Text fw={600}>
                {dependent.subtype === InsuredSubtype.OTHER_DEPENDENT
                  ? 'Dependent'
                  : 'Child'}
                <Text ml="0.25rem" fw={400} c="zorroGray.4" isMasked>
                  ({dependent.firstName} {dependent.lastName})
                </Text>
                {dependent.isDisabled && (
                  <Text
                    ml="var(--space-4)"
                    c="var(--color-text-default-secondary)"
                  >
                    - {disabilityLabel}
                  </Text>
                )}
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <ReadonlyDependentForm
                fields={dependent}
                targetEnrollmentDate={targetEnrollmentDate}
                withAgeAtPlanStart={withAgeAtPlanStart}
                isEmployeeMode={isEmployeeMode}
              />
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};
